import { useState, useCallback, useRef } from 'react';
import { AppNode } from '@/types';

export interface UseNodeMediaOptions {
  data: AppNode;
}

export function useNodeMedia(options: UseNodeMediaOptions) {
  const { data } = options;
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [showImageGrid, setShowImageGrid] = useState(false);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const mediaRef = useRef<HTMLImageElement | HTMLVideoElement | HTMLAudioElement | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (data.data.images && data.data.images.length > 1 || (data.data.videoUris && data.data.videoUris.length > 1)) {
      setShowImageGrid(true);
    }
  }, [data.data.images, data.data.videoUris]);

  const handleMouseLeave = useCallback(() => {
    setShowImageGrid(false);
  }, []);

  const toggleVideoPlay = useCallback(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.paused) {
      video.play().then(() => setIsPlayingVideo(true)).catch(() => {});
    } else {
      video.pause();
      setIsPlayingVideo(false);
    }
  }, []);

  const handleVideoTimeUpdate = useCallback(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    setVideoProgress(video.currentTime);
    setVideoDuration(video.duration || 0);
  }, []);

  const handleVideoSeek = useCallback((time: number) => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    video.currentTime = time;
    setVideoProgress(time);
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsPlayingVideo(false);
    setVideoProgress(0);
  }, []);

  const handleVideoLoadedMetadata = useCallback(() => {
    const video = mediaRef.current;
    if (!(video instanceof HTMLVideoElement)) return;
    setVideoDuration(video.duration || 0);
  }, []);

  return {
    isPlayingAudio,
    setIsPlayingAudio,
    isPlayingVideo,
    setIsPlayingVideo,
    videoProgress,
    videoDuration,
    showImageGrid,
    mediaRef,
    handleMouseEnter,
    handleMouseLeave,
    toggleVideoPlay,
    handleVideoTimeUpdate,
    handleVideoSeek,
    handleVideoEnded,
    handleVideoLoadedMetadata,
  };
}
